import { lazy, Suspense, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { hostBrand, hostInfo, setFavicon, trackVisit, DOCSTATE_FAVICON } from "@/lib/brand";
import { AnimatePresence, motion } from "framer-motion";
import { ContentProvider, useContent } from "@/context/ContentContext";
import { AuthProvider } from "@/context/AuthContext";
import { BookingProvider } from "@/context/BookingContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ImmersiveFooter } from "@/components/immersive/ImmersiveFooter";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { ChatWidget } from "@/components/ChatWidget";
import { BookingModal } from "@/components/BookingModal";
import { ScrollProgress } from "@/components/ScrollProgress";
import { LiquidFX } from "@/components/liquid/LiquidFX";
import { BrandIntro } from "@/components/BrandIntro";
import { AmbientSound } from "@/components/AmbientSound";
import { ConnectionGuard } from "@/components/ConnectionGuard";
import { PushOptIn } from "@/components/PushOptIn";
import { isLightImm } from "@/components/immersive/ImmHeader";
import { Toaster } from "@/components/ui/sonner";
import { applyPreset } from "@/lib/presets";
import Home from "@/pages/Home";
import HomeLiquid from "@/pages/HomeLiquid";
import Treatments from "@/pages/Treatments";
import Doctors from "@/pages/Doctors";
import About from "@/pages/About";
import Gallery from "@/pages/Gallery";
import Contact from "@/pages/Contact";
import Blog from "@/pages/Blog";
import ArticlePage from "@/pages/ArticlePage";
import TreatmentsLiquid from "@/pages/liquid/TreatmentsLiquid";
import DoctorsLiquid from "@/pages/liquid/DoctorsLiquid";
import AboutLiquid from "@/pages/liquid/AboutLiquid";
import GalleryLiquid from "@/pages/liquid/GalleryLiquid";
import BlogLiquid from "@/pages/liquid/BlogLiquid";
import ContactLiquid from "@/pages/liquid/ContactLiquid";
import ArticleLiquid from "@/pages/liquid/ArticleLiquid";
import { RoyalHeader } from "@/components/royal/RoyalHeader";
import { RoyalFooter } from "@/components/royal/RoyalFooter";
import HomeRoyal from "@/pages/royal/HomeRoyal";
import TreatmentsRoyal from "@/pages/royal/TreatmentsRoyal";
import DoctorsRoyal from "@/pages/royal/DoctorsRoyal";
import AboutRoyal from "@/pages/royal/AboutRoyal";
import GalleryRoyal from "@/pages/royal/GalleryRoyal";
import BlogRoyal from "@/pages/royal/BlogRoyal";
import ContactRoyal from "@/pages/royal/ContactRoyal";
import ArticleRoyal from "@/pages/royal/ArticleRoyal";
import { SEOHead } from "@/components/SEOHead";
import { PAGE_SEO_DEFAULTS, medicalClinicSchema } from "@/lib/seo";
import { injectSnippet, trackPageView, initMetaPixel } from "@/lib/analytics";
// Immersive (WebGL/three.js) pages are code-split so classic/liquid visitors
// never pay the 3D bundle cost, and the hero paints before the scene parses.
const HomeImmersive = lazy(() => import("@/pages/immersive/HomeImmersive"));
const TreatmentsImmersive = lazy(() => import("@/pages/immersive/TreatmentsImmersive"));
const DoctorsImmersive = lazy(() => import("@/pages/immersive/DoctorsImmersive"));
const AboutImmersive = lazy(() => import("@/pages/immersive/AboutImmersive"));
const GalleryImmersive = lazy(() => import("@/pages/immersive/GalleryImmersive"));
const BlogImmersive = lazy(() => import("@/pages/immersive/BlogImmersive"));
const ContactImmersive = lazy(() => import("@/pages/immersive/ContactImmersive"));
const ArticleImmersive = lazy(() => import("@/pages/immersive/ArticleImmersive"));
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import DocstatePortal from "@/pages/docstate/DocstatePortal";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const FaviconSync = () => {
  const { content } = useContent();
  useEffect(() => {
    // On the Docstate domain/portal the portal manages its own favicon.
    if (hostBrand() === "docstate" || window.location.pathname.startsWith("/docstate")) return;
    const url = content?.settings?.favicon_url;
    if (!url) return;
    setFavicon(url);
  }, [content]);
  return null;
};

// Set the correct favicon immediately on the live domains (covers the first
// paint / redirect flash before the brand's own portal mounts).
const BrandBootstrap = () => {
  useEffect(() => {
    if (hostBrand() === "docstate") setFavicon(DOCSTATE_FAVICON);
  }, []);
  return null;
};

// Decides what the root path renders based on the live host.
// Admin hosts (admin.* / app.*) send root to the login; client/preview hosts
// show the public site. /admin itself is always reachable on every domain.
const RootArea = () => {
  const { brand, type } = hostInfo();
  if (type === "admin") return <Navigate to="/admin/login" replace />;
  if (brand === "docstate" && type === "client") return <DocstatePortal />;
  return <PublicLayout />;
};

const PresetSync = () => {
  const { content } = useContent();
  useEffect(() => {
    const p = content?.settings?.design_preset;
    if (p) applyPreset(p);
  }, [content]);
  return null;
};

// Route-keyed SEO: per-page meta from Site Settings (SEO tab) over curated
// defaults, plus global MedicalClinic schema, GTM and GSC verification.
const ROUTE_SEO = {
  "/": "home",
  "/treatments": "treatments",
  "/doctors": "doctors",
  "/about": "about",
  "/gallery": "gallery",
  "/blog": "blog",
  "/contact": "contact",
};

const GlobalSEO = () => {
  const { content } = useContent();
  const location = useLocation();
  const s = content?.settings || {};
  const pageKey = ROUTE_SEO[location.pathname];
  const custom = (s.seo_pages || {})[pageKey] || {};
  const def = PAGE_SEO_DEFAULTS[pageKey] || {};
  const firstRoute = useRef(true);

  // SPA page_view tracking (gtag config already fired the initial view)
  useEffect(() => {
    trackVisit("clinic");
    if (firstRoute.current) {
      firstRoute.current = false;
      return;
    }
    trackPageView(location.pathname);
    if (typeof window.fbq === "function") window.fbq("track", "PageView");
  }, [location.pathname]);

  // admin-managed third-party tags & pixels
  useEffect(() => {
    (content?.custom_tags || []).forEach((t) => injectSnippet(t.id, t.code, t.location));
  }, [JSON.stringify(content?.custom_tags || [])]);

  useEffect(() => {
    const ga4 = (s.ga4_id || "").trim();
    if (ga4 && !document.getElementById("ga-tag")) {
      // GA4 / Google tag (gtag.js)
      const el = document.createElement("script");
      el.id = "ga-tag";
      el.async = true;
      el.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ga4)}`;
      document.head.appendChild(el);
      window.dataLayer = window.dataLayer || [];
      window.gtag =
        window.gtag ||
        function gtag() {
          window.dataLayer.push(arguments);
        };
      window.gtag("js", new Date());
      window.gtag("config", ga4);
    }
    const gtm = (s.gtm_id || "").trim();
    if (gtm && !document.getElementById("gtm-tag")) {
      // Google Tag Manager loader
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
      const el = document.createElement("script");
      el.id = "gtm-tag";
      el.async = true;
      el.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtm)}`;
      document.head.appendChild(el);
    }
    // Google Ads tag — rides the gtag loader (loads it if GA4 is absent)
    const adsId = (s.google_ads_id || "").trim();
    if (adsId) {
      window.__googleAdsId = adsId;
      window.__googleAdsLabel = (s.google_ads_label || "").trim();
      if (!document.getElementById("ga-tag")) {
        const el = document.createElement("script");
        el.id = "ga-tag";
        el.async = true;
        el.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(adsId)}`;
        document.head.appendChild(el);
        window.dataLayer = window.dataLayer || [];
        window.gtag =
          window.gtag ||
          function gtag() {
            window.dataLayer.push(arguments);
          };
        window.gtag("js", new Date());
      }
      window.gtag && window.gtag("config", adsId);
    }
    // Meta Pixel
    const pixel = (s.meta_pixel_id || "").trim();
    if (pixel) initMetaPixel(pixel);
    if (s.gsc_verification) {
      let el = document.head.querySelector('meta[name="google-site-verification"]');
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", "google-site-verification");
        document.head.appendChild(el);
      }
      el.setAttribute("content", s.gsc_verification);
    }
  }, [s.ga4_id, s.gtm_id, s.google_ads_id, s.google_ads_label, s.meta_pixel_id, s.gsc_verification]);

  const clinic = medicalClinicSchema(s, window.location.origin);
  if (!pageKey) return <SEOHead jsonLd={[clinic]} />;
  return (
    <SEOHead
      title={custom.title || def.title}
      description={custom.description || def.description}
      path={location.pathname}
      image={s.brand_lockup_url}
      jsonLd={[clinic]}
    />
  );
};

const DesignSwitch = ({ classic: Classic, liquid: Liquid, immersive: Immersive, royal: Royal }) => {
  const { content } = useContent();
  const v = content?.settings?.design_variant;
  if (v === "liquid") return <Liquid />;
  if (v === "royal") return <Royal />;
  if (v === "immersive")
    return (
      <Suspense
        fallback={
          <div
            className="min-h-screen"
            style={{ background: isLightImm(content?.settings) ? "#F5F0E8" : "#0C0A09" }}
            data-testid="immersive-loading"
          />
        }
      >
        <Immersive />
      </Suspense>
    );
  return <Classic />;
};

const PublicLayout = () => {
  const location = useLocation();
  const { content } = useContent();
  const liquid = content?.settings?.design_variant === "liquid";
  const immersive = content?.settings?.design_variant === "immersive";
  const royal = content?.settings?.design_variant === "royal";
  const immLight = isLightImm(content?.settings);

  useEffect(() => {
    document.body.classList.toggle("imm-dark", immersive && !immLight);
    document.body.classList.toggle("imm-light", immersive && immLight);
    document.body.classList.toggle("imm-body", !!immersive);
    return () => document.body.classList.remove("imm-dark", "imm-light", "imm-body");
  }, [immersive, immLight]);
  return (
    <>
      {liquid && <LiquidFX />}
      <GlobalSEO />
      <ScrollProgress />
      <BrandIntro />
      {content?.settings?.sound_enabled !== "off" && <AmbientSound />}
      <ConnectionGuard />
      <PushOptIn />
      {royal ? <RoyalHeader /> : <Navbar />}
      <AnimatePresence mode="wait">
        <motion.main
          key={location.pathname}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
          <Routes location={location}>
            <Route path="/" element={<DesignSwitch classic={Home} liquid={HomeLiquid} immersive={HomeImmersive} royal={HomeRoyal} />} />
            <Route path="/treatments" element={<DesignSwitch classic={Treatments} liquid={TreatmentsLiquid} immersive={TreatmentsImmersive} royal={TreatmentsRoyal} />} />
            <Route path="/doctors" element={<DesignSwitch classic={Doctors} liquid={DoctorsLiquid} immersive={DoctorsImmersive} royal={DoctorsRoyal} />} />
            <Route path="/about" element={<DesignSwitch classic={About} liquid={AboutLiquid} immersive={AboutImmersive} royal={AboutRoyal} />} />
            <Route path="/gallery" element={<DesignSwitch classic={Gallery} liquid={GalleryLiquid} immersive={GalleryImmersive} royal={GalleryRoyal} />} />
            <Route path="/blog" element={<DesignSwitch classic={Blog} liquid={BlogLiquid} immersive={BlogImmersive} royal={BlogRoyal} />} />
            <Route path="/blog/:articleId" element={<DesignSwitch classic={ArticlePage} liquid={ArticleLiquid} immersive={ArticleImmersive} royal={ArticleRoyal} />} />
            <Route path="/contact" element={<DesignSwitch classic={Contact} liquid={ContactLiquid} immersive={ContactImmersive} royal={ContactRoyal} />} />
            <Route path="*" element={<DesignSwitch classic={Home} liquid={HomeLiquid} immersive={HomeImmersive} royal={HomeRoyal} />} />
          </Routes>
        </motion.main>
      </AnimatePresence>
      {immersive ? <ImmersiveFooter /> : royal ? <RoyalFooter /> : <Footer />}
      <WhatsAppFloat />
      <ChatWidget />
    </>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ContentProvider>
            <BookingProvider>
              <ScrollToTop />
              <FaviconSync />
              <BrandBootstrap />
              <PresetSync />
              <Routes>
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin/*" element={<AdminDashboard />} />
                <Route path="/docstate/*" element={<DocstatePortal />} />
                <Route path="/*" element={<RootArea />} />
              </Routes>
              <BookingModal />
              <Toaster position="top-center" richColors />
            </BookingProvider>
          </ContentProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
